import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {saveAppLanguage} from './i18n/detectLanguage.js';
import LandingHero from './LandingHero.jsx';
import LandingNutrition from './LandingNutrition.jsx';
import LandingSections from './LandingSections.jsx';
import './LandingFullPreview.css';

const INSTALL = {
  ru: {
    android:{headline:'НА ANDROID',subtitle:'Добавь FitMeUp на главный экран',steps:[['Открой меню браузера','Нажми на значок меню (⋮) справа от адресной строки.'],['Найди установку','Выбери «Установить приложение» или «Добавить на главный экран».'],['Подтверди','Нажми «Установить» или «Добавить». FitMeUp появится на главном экране.']]},
    ios:{headline:'НА iOS',subtitle:'Добавь FitMeUp на экран «Домой»',steps:[['Открой в Safari','Если сайт открыт внутри Telegram, сначала открой его в Safari.'],['Нажми «Поделиться»','В Safari нажми на значок «Поделиться» и найди «На экран Домой».'],['Подтверди','Нажми «Добавить». Значок FitMeUp появится на экране «Домой».']]},
    done:'Понятно',support:'Поддержка',menu:'Меню',nutrition:'Питание',workouts:'Тренировки',progress:'Прогресс',androidLabel:'Установить на Android',iosLabel:'Установить на iOS',close:'Закрыть'
  },
  en: {
    android:{headline:'ON ANDROID',subtitle:'Add FitMeUp to your home screen',steps:[['Open browser menu','Tap the menu icon (⋮) next to the address bar.'],['Choose installation','Select “Install app” or “Add to Home screen”.'],['Confirm','Tap “Install” or “Add” to place FitMeUp on your home screen.']]},
    ios:{headline:'ON iOS',subtitle:'Add FitMeUp to your Home Screen',steps:[['Open in Safari','If you are viewing this inside Telegram, open the site in Safari first.'],['Tap Share','In Safari, tap Share and choose “Add to Home Screen”.'],['Confirm','Tap “Add” to place FitMeUp on your Home Screen.']]},
    done:'Got it',support:'Support',menu:'Menu',nutrition:'Nutrition',workouts:'Workouts',progress:'Progress',androidLabel:'Install on Android',iosLabel:'Install on iOS',close:'Close'
  }
};
function CloseIcon(){return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>}
function Overlay({children,onClose,menu=false,closeLabel}){
 const modalRef=useRef(null);
 useEffect(()=>{
   const onKey=e=>{if(e.key==='Escape')onClose()};
   const html=document.documentElement,body=document.body;
   const scrollY=window.scrollY;
   const previous={htmlOverflow:html.style.overflow,bodyPosition:body.style.position,bodyTop:body.style.top,bodyWidth:body.style.width,bodyOverflow:body.style.overflow};
   document.addEventListener('keydown',onKey);
   html.style.overflow='hidden'; body.style.overflow='hidden';
   body.style.position='fixed';body.style.top=`-${scrollY}px`;body.style.width='100%';
   return ()=>{
     document.removeEventListener('keydown',onKey);
     html.style.overflow=previous.htmlOverflow;
     body.style.position=previous.bodyPosition;body.style.top=previous.bodyTop;
     body.style.width=previous.bodyWidth;body.style.overflow=previous.bodyOverflow;
     window.scrollTo(0,scrollY);
   };
 },[onClose]);
 useLayoutEffect(()=>{
   if(menu)return;
   const panel=modalRef.current;
   if(!panel)return;
   let raf=0;
   const fit=()=>{
     cancelAnimationFrame(raf);
     raf=requestAnimationFrame(()=>{
       panel.style.zoom='1';
       // Visual viewport accounts for dynamic Safari bars and on-screen keyboard.
       const vh=window.visualViewport?.height||window.innerHeight;
       const room=Math.max(220,vh-24);
       const h=panel.scrollHeight;
       panel.style.zoom=String(Math.min(1,room/Math.max(1,h)));
     });
   };
   fit();
   window.addEventListener('resize',fit);
   window.visualViewport?.addEventListener('resize',fit);
   // Images may load after the first layout.
   panel.querySelectorAll('img').forEach(img=>img.addEventListener('load',fit));
   return ()=>{cancelAnimationFrame(raf);window.removeEventListener('resize',fit);window.visualViewport?.removeEventListener('resize',fit);panel.querySelectorAll('img').forEach(img=>img.removeEventListener('load',fit))};
 },[menu,children]);
 return createPortal(<div className={'flp-overlay '+(menu?'flp-overlay-menu':'flp-overlay-guide')} onMouseDown={e=>{if(e.target===e.currentTarget)onClose()}}><div ref={modalRef} className="flp-modal" role="dialog" aria-modal="true"><button className="flp-modal-close" type="button" aria-label={closeLabel} onClick={onClose}><CloseIcon/></button>{children}</div></div>,document.body);
}
export default function LandingFullPreview(){
 const {i18n}=useTranslation();
 const initialLanguage=(i18n.resolvedLanguage||i18n.language||'ru').toLowerCase().startsWith('en')?'en':'ru';
 const [language,setLanguage]=useState(initialLanguage);
 const [menu,setMenu]=useState(false);
 const [guide,setGuide]=useState(null);
 const en=language==='en';const t=INSTALL[language];
 useEffect(()=>{setLanguage((i18n.resolvedLanguage||i18n.language||'ru').toLowerCase().startsWith('en')?'en':'ru')},[i18n.resolvedLanguage,i18n.language]);
 const changeLanguage=next=>{const normalized=String(next).toLowerCase().startsWith('en')?'en':'ru';setLanguage(normalized);saveAppLanguage(normalized);void i18n.changeLanguage(normalized)};
 const go=id=>{setMenu(false);requestAnimationFrame(()=>document.getElementById(id)?.scrollIntoView({behavior:'smooth',block:'start'}))};
 const openGuide = name => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', name === 'ios' ? 'install_ios_click' : 'install_android_click', {
      install_platform: name,
      event_category: 'installation'
    });
  }

  setMenu(false);
  setGuide(name);
};
 return <div className="flp-root">
  <LandingHero language={language} onLanguageChange={changeLanguage} onMenuClick={()=>setMenu(true)} onInstall={openGuide}/>
  <LandingNutrition language={language} imageSrc="/landing/food-bowl.webp"/>
  <LandingSections language={language} onInstall={openGuide}/>

  <div style={{
    textAlign: 'center',
    padding: '20px 16px 30px',
    background: '#001321'
  }}>
    <a
      href={en ? '/privacy/index-en.html' : '/privacy/index-ru.html'}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: '#8daac5',
        fontSize: '13px',
        textDecoration: 'underline'
      }}
    >
      {en ? 'Privacy Policy' : 'Политика конфиденциальности'}
    </a>
  </div>

  {menu&&<Overlay menu onClose={()=>setMenu(false)} closeLabel={t.close}><div className="flp-modal-overline">FitMe<span>Up</span></div><h2>{t.menu}</h2><nav className="flp-menu-links" aria-label="FitMeUp"><button onClick={()=>go('landing-nutrition')}><span className="flp-menu-number">01</span>{t.nutrition}<span className="flp-menu-chevron">↗</span></button><button onClick={()=>go('landing-workout')}><span className="flp-menu-number">02</span>{t.workouts}<span className="flp-menu-chevron">↗</span></button><button onClick={()=>go('landing-progress')}><span className="flp-menu-number">03</span>{t.progress}<span className="flp-menu-chevron">↗</span></button><div className="flp-menu-rule"/><button onClick={()=>openGuide('android')}><span className="flp-menu-number">↘</span>{t.androidLabel}</button><button onClick={()=>openGuide('ios')}><span className="flp-menu-number">↘</span>{t.iosLabel}</button><a href="https://t.me/FitMeUp_support" target="_blank" rel="noopener noreferrer" onClick={()=>setMenu(false)}><span className="flp-menu-number">?</span>{t.support}<span className="flp-menu-chevron">↗</span></a></nav></Overlay>}
  {guide&&<Overlay onClose={()=>setGuide(null)} closeLabel={t.close}>
    <div className="flp-guide-heading"><div className="flp-guide-brand">FitMe<span>Up</span></div><strong className="flp-guide-platform">{guide==='ios'?'iOS':'Android'}</strong></div>
    <div className="flp-guide-intro">{guide==='ios'?(en?'Install FitMeUp on your iPhone':'Установи FitMeUp на iPhone'):(en?'Install FitMeUp on Android':'Установи FitMeUp на Android')}</div>
    <div
  style={{
    margin: "12px 0 16px",
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid rgba(130,170,202,0.25)",
    background: "rgba(100,150,190,0.10)",
    fontSize: 13,
    lineHeight: 1.5,
  }}
>
  <strong>
    {en ? "Before installing" : "Перед установкой"}
  </strong>

  <div style={{ marginTop: 5 }}>
    {guide === "ios"
      ? en
        ? "Open Safari on your iPhone and go to fitmeup.fit. Then follow the steps below."
        : "Открой Safari на своём iPhone и перейди на fitmeup.fit. Затем следуй инструкции ниже."
      : en
        ? "Open Google Chrome on your phone and go to fitmeup.fit. Then follow the steps below."
        : "Открой Google Chrome на своём телефоне и перейди на fitmeup.fit. Затем следуй инструкции ниже."}
  </div>
</div>
    
    <div className="flp-guide-step-list">
      {(guide==='ios'
       ? (en?[
          ['Open menu and Share','Tap the three dots (⋯) in the browser toolbar, then select Share.'],
          ['Add to Home Screen','Scroll down in the share sheet (tap Show More if necessary) and select Add to Home Screen.'],
          ['Confirm','Keep the name FitMeUp and tap Add.']
         ]:[
          ['Открой меню и поделись','Нажми три точки (⋯) на панели браузера, затем выбери «Поделиться».'],
          ['На экран «Домой»','Прокрути список вниз (при необходимости нажми «Показать больше») и выбери «На экран Домой».'],
          ['Подтверди','Оставь название FitMeUp и нажми «Добавить».']
         ])
       : (en?[
          ['Open browser menu','Tap the three-dot menu (⋮) in your browser.'],
          ['Choose installation','Select Install app or Add to Home screen.'],
          ['Confirm','Tap Install or Add to finish.']
         ]:[
          ['Открой меню браузера','Нажми на три точки (⋮) в браузере.'],
          ['Выбери установку','Нажми «Установить приложение» или «Добавить на главный экран».'],
          ['Подтверди','Нажми «Установить» или «Добавить».']
         ])
      ).map(([title,description],index)=><article className="flp-visual-step" key={index}>
        <div className="flp-step-head"><b>{index+1}</b><div><h3>{title}</h3><p>{description}</p></div></div>
        {(index<2)&&<div className="flp-step-screenshot" role="img" aria-label={title}>
          <img
  src={
    guide === 'android'
      ? `/landing/steps/${
          index === 1 && en
            ? 'android-install-en'
            : ['android-menu', 'android-install', 'android-confirm'][index]
        }.png`
      : `/landing/steps/${
          index === 1 && en
            ? 'ios-home-en'
            : ['ios-menu', 'ios-home'][index]
        }.png`
  }
  alt=""
  loading="lazy"
/>
        </div>}
        {(index===2)&&<div className="flp-confirmation-preview"><img src="/apple-touch-icon.png" alt="FitMeUp"/><div><strong>FitMeUp</strong><small>fitmeup.fit</small></div><span>{guide==='android' ? (en?'Install':'Установить') : (en?'Add':'Добавить')}</span></div>}
      </article>)}
    </div>
    <button className="flp-modal-done" type="button" onClick={()=>setGuide(null)}>{t.done}</button>
   </Overlay>}
 </div>;
}
